import type { MedicineMovement } from '@/types';

/** The translation and formatting a "Trukket fra" line needs, injected so the wording is testable without the i18n plugin. */
export type DrawnFromFormat = {
    t: (key: string, params?: Record<string, string>) => string;
    /** A quantity as the tables show it, without its unit. */
    n: (value: number) => string;
    /** A yyyy-MM-dd day as the tables show dates. */
    date: (day: string) => string;
};

export type DrawnFromLine = {
    text: string;
    /** `uncovered` reads red (consumption no purchase covered); `note` reads muted. */
    tone: 'normal' | 'uncovered' | 'note';
};

const KEY_PREFIX = 'views.health.inventory.movements.drawnFrom.';

/**
 * What a movement drew from, one line per lot (a purchase with its batch number, or a count) and how
 * much of it, plus a line for any part no lot covered or a purchase covered of earlier consumption.
 * Empty when there is nothing to say.
 */
export function drawnFromLines(movement: MedicineMovement, { t, n, date }: DrawnFromFormat): DrawnFromLine[] {
    const lines: DrawnFromLine[] = movement.drawn_from.map((lot) => {
        const label =
            lot.source === 'count'
                ? t(`${KEY_PREFIX}count`, { date: date(lot.date) })
                : t(lot.batch_number ? `${KEY_PREFIX}purchaseBatch` : `${KEY_PREFIX}purchase`, { date: date(lot.date), batch: lot.batch_number ?? '' });

        return { text: `${label} · ${n(lot.quantity)} ${movement.unit_name}`, tone: 'normal' };
    });

    if (movement.uncovered > 0) {
        lines.push({ text: t(`${KEY_PREFIX}uncovered`, { quantity: `${n(movement.uncovered)} ${movement.unit_name}` }), tone: 'uncovered' });
    }
    if (movement.covered_deficit > 0) {
        lines.push({ text: t(`${KEY_PREFIX}coversDeficit`, { quantity: `${n(movement.covered_deficit)} ${movement.unit_name}` }), tone: 'note' });
    }

    return lines;
}
